/**
 * Catches render-time throws so a crash degrades to a readable message instead
 * of a blank white page.
 *
 * Mounted twice (see docs/superpowers/specs/2026-07-16-error-boundary-design.md):
 *   - main.tsx  <ErrorBoundary category="Root">  — backstop for providers + Layout
 *   - App.tsx   <ErrorBoundary category="Route" resetKey={location.key}> — keeps the sidebar alive
 *
 * Scope: render/lifecycle/constructor throws only. Event handlers, promise
 * rejections and async Supabase calls are NOT caught by any error boundary —
 * those still need explicit error state in the fetching paths.
 */

import React, { Component, type ErrorInfo, type ReactNode } from "react";
import i18n from "../i18n";
import { logError } from "../services/logger";

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Replaces the built-in fallback. Must not throw — React cannot catch a throw from a boundary's own fallback. */
  fallback?: ReactNode;
  /** Clears a showing error when this changes. Pass location.key to recover on navigation. */
  resetKey?: string;
  /** Log category, surfaced verbatim in the LogViewer filter (Ctrl+Shift+L). */
  category?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * i18next returns the raw key string when a lookup misses, which is truthy and
 * would print "errorBoundary.title" at the user — hence the `v !== key` check.
 * The English literal is the floor: useTranslation() can suspend (there is no
 * <Suspense> in this app) and a throw inside a fallback is uncatchable, so the
 * fallback reads the singleton directly and degrades to English instead.
 */
const safeT = (key: string, fallback: string): string => {
  try {
    const v: unknown = i18n.t(key);
    return typeof v === "string" && v !== "" && v !== key ? v : fallback;
  } catch {
    return fallback;
  }
};

// Inline styles, no imports: this fallback must render outside Layout and
// outside Router context, where Tailwind classes and <Link> are not safe.
const wrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "60vh",
  padding: "2rem 1rem",
  textAlign: "center",
  fontFamily: "Prompt, system-ui, sans-serif",
};

const titleStyle: React.CSSProperties = {
  fontSize: "1.125rem",
  fontWeight: 600,
  color: "#111827",
  margin: "0 0 0.5rem",
};

const messageStyle: React.CSSProperties = {
  fontSize: "0.875rem",
  color: "#6b7280",
  maxWidth: "28rem",
  margin: "0 0 1.5rem",
};

const buttonStyle: React.CSSProperties = {
  background: "#111827",
  color: "#ffffff",
  border: "none",
  borderRadius: "0.5rem",
  padding: "0.625rem 1.25rem",
  fontSize: "0.875rem",
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "inherit",
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // componentStack goes in the 4th `data` arg on purpose: logger.error guards
    // the 3rd with `instanceof Error` and silently drops anything else, so
    // passing errorInfo there would lose the component stack without a warning.
    logError(this.props.category ?? "ErrorBoundary", "Uncaught render error", error, {
      componentStack: errorInfo.componentStack,
    });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Guarded on hasError so this is inert on the happy path — no remount cost
    // for pages that navigate on interaction (e.g. Settings tabs).
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  private handleReload = (): void => {
    // assign('/'), not reload(): reload would re-run the URL that just crashed.
    window.location.assign("/");
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div style={wrapStyle} role="alert">
        <h1 style={titleStyle}>{safeT("errorBoundary.title", "Something went wrong")}</h1>
        <p style={messageStyle}>
          {safeT(
            "errorBoundary.message",
            "This page ran into an unexpected error. Your data is safe — try going back to the dashboard."
          )}
        </p>
        <button type="button" style={buttonStyle} onClick={this.handleReload}>
          {safeT("errorBoundary.reload", "Back to dashboard")}
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
